import type { ThreadData } from "@liveblocks/core";

/**
 * Compute whether a thread is unread for the current user. Priority:
 *  1. Liveblocks subscription (cross-browser authoritative; set by
 *     `markThreadAsRead`, which auto-subscribes on first open).
 *       - status === "subscribed", unreadSince === null → fully read
 *       - status === "subscribed", unreadSince is Date  → unread from that
 *         date; compare against latest non-self comment.
 *  2. Local `threadReadAt` — fast-path while Liveblocks hydrates and fallback
 *     for threads the user isn't subscribed to.
 *  3. Nothing → thread never opened from any browser → unread.
 *
 * Activity only from the current user does NOT count as unread.
 */
export const computeIsUnread = (
  thread: ThreadData,
  selfId: string | undefined,
  subscription: any,
  localReadAt: number | undefined
): boolean => {
  if (thread.resolved) return false;
  const latestOtherComment = [...thread.comments].reverse().find((c) => c.body && c.userId !== selfId);
  if (!latestOtherComment) return false;
  const latestTs = new Date(latestOtherComment.createdAt).getTime();

  if (subscription?.status === "subscribed") {
    const unreadSince = subscription.unreadSince;
    if (unreadSince) return latestTs > new Date(unreadSince).getTime();
    // Subscribed but Liveblocks hasn't pushed an `unreadSince` for this new
    // activity yet (replies / @-mentions don't always trigger a real-time
    // subscription update). Fall through to the local read map so the badge
    // still appears the moment the new comment hits the room.
  }

  if (typeof localReadAt === "number") return latestTs > localReadAt;
  return true;
};
